/**
 * React Query hooks for Recruitment / ATS data fetching.
 * 
 * All hooks use hierarchical query keys for granular invalidation:
 * ['recruitment', 'applications', params]
 * ['recruitment', 'application', id]
 * ['recruitment', 'jobs', params]
 * etc.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recruitmentAPI } from '../services/api';
import toast from '../utils/toast';


// ---------------------------------------------------------------------------
// Applications
// ---------------------------------------------------------------------------

/**
 * Hook to fetch applications list with optional filters.
 * 
 * @param {Object} params - Query parameters (state, department, archived, search)
 * @returns {Object} React Query result
 */
export const useApplicationsList = (params = {}) => {
  return useQuery({
    queryKey: ['recruitment', 'applications', params],
    queryFn: async () => {
      const response = await recruitmentAPI.listApplications(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch single application detail.
 * 
 * @param {number} id - Application ID
 * @returns {Object} React Query result
 */
export const useApplicationDetail = (id) => {
  return useQuery({
    queryKey: ['recruitment', 'application', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await recruitmentAPI.getApplication(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to create a new application.
 * 
 * @returns {Object} React Query mutation
 */
export const useCreateApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => recruitmentAPI.createApplication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
      toast.success('Application created successfully');
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail || 'Failed to create application';
      toast.error(detail);
    },
  });
};

/**
 * Hook to update an application.
 * 
 * @returns {Object} React Query mutation
 */
export const useUpdateApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => recruitmentAPI.updateApplication(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'application', variables.id] });
      toast.success('Application updated successfully');
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail || 'Failed to update application';
      toast.error(detail);
    },
  });
};

/**
 * Hook to transition application state.
 * 
 * @returns {Object} React Query mutation
 */
export const useTransitionApplicationState = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_state, reason }) =>
      recruitmentAPI.transitionState(id, { new_state, reason }),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'application', variables.id] });
      toast.success('Application state updated');
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail || 'Failed to transition state';
      toast.error(detail);
    },
  });
};

/**
 * Hook to archive an application.
 * 
 * @returns {Object} React Query mutation
 */
export const useArchiveApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => recruitmentAPI.archiveApplication(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'application', id] });
      toast.success('Application archived');
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail || 'Failed to archive application';
      toast.error(detail);
    },
  });
};

/**
 * Hook to fetch application state history.
 * 
 * @param {number} id - Application ID
 * @returns {Object} React Query result
 */
export const useApplicationStateHistory = (id) => {
  return useQuery({
    queryKey: ['recruitment', 'application', id, 'history'],
    queryFn: async () => {
      if (!id) return [];
      const response = await recruitmentAPI.getStateHistory(id);
      return response.data || [];
    },
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/**
 * Hook to fetch jobs list with optional filters.
 * 
 * @param {Object} params - Query parameters (status, department_id, etc.)
 * @returns {Object} React Query result
 */
export const useJobsList = (params = {}) => {
  return useQuery({
    queryKey: ['recruitment', 'jobs', params],
    queryFn: async () => {
      const response = await recruitmentAPI.listJobs(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (jobs change less frequently)
  });
};

/**
 * Hook to fetch single job detail.
 * 
 * @param {number} id - Job ID
 * @returns {Object} React Query result
 */
export const useJobDetail = (id) => {
  return useQuery({
    queryKey: ['recruitment', 'job', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await recruitmentAPI.getJob(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------

/**
 * Hook to fetch reviews for an application.
 * 
 * @param {number} applicationId - Application ID
 * @returns {Object} React Query result
 */
export const useReviewsList = (applicationId) => {
  return useQuery({
    queryKey: ['recruitment', 'reviews', { application: applicationId }],
    queryFn: async () => {
      if (!applicationId) return [];
      const response = await recruitmentAPI.listReviews({ application: applicationId });
      return response.data?.results || response.data || [];
    },
    enabled: !!applicationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to create a review.
 * 
 * @returns {Object} React Query mutation
 */
export const useCreateReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => recruitmentAPI.createReview(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
      if (response.data?.application) {
        queryClient.invalidateQueries({
          queryKey: ['recruitment', 'application', response.data.application],
        });
      }
      toast.success('Review submitted successfully');
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail || 'Failed to submit review';
      toast.error(detail);
    },
  });
};

// ---------------------------------------------------------------------------
// Interview Slots
// ---------------------------------------------------------------------------

/**
 * Hook to fetch interview slots for an application.
 * 
 * @param {number} applicationId - Application ID
 * @returns {Object} React Query result
 */
export const useInterviewSlotsList = (applicationId) => {
  return useQuery({
    queryKey: ['recruitment', 'interview-slots', { application: applicationId }],
    queryFn: async () => {
      if (!applicationId) return [];
      const response = await recruitmentAPI.listInterviewSlots({ application: applicationId });
      return response.data?.results || response.data || [];
    },
    enabled: !!applicationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ---------------------------------------------------------------------------
// Offer Letters
// ---------------------------------------------------------------------------

/**
 * Hook to fetch offer letters for an application.
 * 
 * @param {number} applicationId - Application ID
 * @returns {Object} React Query result
 */
export const useOfferLettersList = (applicationId) => {
  return useQuery({
    queryKey: ['recruitment', 'offer-letters', { application: applicationId }],
    queryFn: async () => {
      if (!applicationId) return [];
      const response = await recruitmentAPI.listOfferLetters({ application: applicationId });
      return response.data?.results || response.data || [];
    },
    enabled: !!applicationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
